/**
 * LITERAL light semantic tokens — the raw colour values (dark mode, step 1).
 *
 * UI code does NOT import this: it imports `semanticTokens.js`, whose colour values are
 * `var(--cvx-<group>-<key>)` strings that switch with the theme. This file is the source the
 * Light palette is built from (`themePalettes.js`), and the only place a consumer that needs a
 * REAL colour (colour math, canvas, a PDF) may read one — via `lightLiteral` from semanticTokens.
 */

import { slate, accent, green, red, amber, blue, violet, teal, indigo, pink, sky, platformNavy } from './primitiveTokens';
import { extended } from './semanticLiteral.extended';

const fontFamily = {
  sans: "'Hanken Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  display: "'Bricolage Grotesque', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  mono: "'IBM Plex Mono', ui-monospace, 'SFMono-Regular', Menlo, Monaco, Consolas, monospace",
};

/** Page + panel backgrounds */
export const bg = {
  canvas: slate[50],
  surface: '#ffffff',
  surfaceMuted: slate[100],
  /** A full PAGE ground that is grey in Light (slate-100, = surfaceMuted) and the black canvas in Dark — never a fill or chip. */
  pageMuted: slate[100],
  /** The desktop frame behind the rounded page card and the phone/tablet menu panel (Light = slate-100). */
  frame: slate[100],
  surfaceElevated: '#ffffff',
  inverse: slate[800],
  /** Soft page wash behind hero areas (Graphite: a hair above the canvas). */
  wash: '#eaf1fa',
  /** Faint ink wash for hover / pressed rows and menu items (Graphite: white at the same weight). */
  tintHover: 'rgba(15, 23, 42, 0.05)',
  /** Track under a thin progress bar on a grey lane. */
  tintTrack: 'rgba(15, 23, 42, 0.08)',
  /** Scroll thumb on a scroll-affordance fade (rest / hover). */
  scrollThumb: 'rgba(15, 23, 42, 0.28)',
  scrollThumbHover: 'rgba(15, 23, 42, 0.40)',
  /** Page scrollbar (index.css): track thumb at rest / hover / drag. */
  scrollbar: 'rgba(148, 163, 184, 0.25)',
  scrollbarHover: 'rgba(100, 116, 139, 0.5)',
  scrollbarActive: 'rgba(71, 85, 105, 0.65)',
  scrollbarDrag: 'rgba(51, 65, 85, 0.75)',
  /** Dark glass pill on the canvas (offline / info bar): Light ink at 88%. */
  inverseGlass: 'rgba(15, 23, 42, 0.88)',
  /** Solid ink button / chip fill (Light: near-black; Graphite flips to light so the label stays readable). */
  inkFill: slate[900],
  /** Hover on a `bg.inkFill` button (Light: one step lighter ink, = bg.inverse). */
  inkFillHover: slate[800],
  /** Translucent progress / pill overlay on a SOLID brand or danger fill that carries white text. */
  onFillOverlay: 'rgba(255, 255, 255, 0.2)',
  onFillOverlayStrong: 'rgba(255, 255, 255, 0.22)',
};

/** Foreground / text */
export const fg = {
  default: slate[900],
  secondary: slate[600],
  // Small grey TEXT (KAN-793): slate-500 = 4.76:1 on white, 4.55:1 on slate-50.
  muted: slate[500],
  /** Between default and secondary (dashboard ink2, slate-700). */
  ink2: slate[700],
  subtle: slate[500],
  // Placeholders + disabled text keep slate-400 (decision, dark-mode step 1: unchanged).
  // ICONS/chevrons: #8391a6 = 3.20:1 on white and 3.06:1 on slate-50 (was slate-400, 2.56:1) — WCAG 1.4.11.
  icon: '#8391a6',
  placeholder: slate[400],
  /** Quiet but READABLE text (zero counts, faint meta): Light = placeholder grey; Dark lifts to a readable grey. */
  faint: slate[400],
  disabled: slate[400],
  inverse: '#ffffff',
  /** Text on `bg.inverseGlass`. */
  inverseSoft: '#f8fafc',
  /** Label on `bg.inkFill`. */
  onInkFill: '#ffffff',
  onPrimary: '#ffffff',
};

/** Borders */
export const border = {
  default: slate[200],
  subtle: slate[100],
  strong: slate[300],
  focus: accent[500],
  // Typing-field edge in Light = the soft hairline the app always had (founder, 2026-10-30, KAN-1144): the 3:1 grey
  // (#8794a6) read as a hard dark outline. Fields stay findable by their white fill + label, and focus is the brand ring.
  field: slate[200],
};

/** Brand / accent (restrained, premium SaaS) */
export const brand = {
  primary: accent[700],
  primaryHover: accent[800],
  primaryMuted: accent[50],
  /** A step stronger than primaryMuted (accent 100) — chip/tile on a muted brand fill. */
  primaryMutedStrong: accent[100],
  /** Brand blue as TEXT/links on a surface: same as `primary` in Light; lifts to a readable blue in Graphite. */
  text: accent[700],
  /** Thin edge around a selected row (RD-UI-044) — pairs with the primaryMuted fill. */
  primaryMutedBorder: accent[200],
  onPrimary: '#ffffff',
  /** Stop Coral — the brand's "arrived" stop (Route V mark, landing page).
      Accent only: the end of a route, never text or a fill for UI chrome. */
  stop: '#FF6A3D',
  /** Stop Coral darkened for small text/fills behind white text (4.83:1 on white; `stop` is 2.85:1). */
  stopStrong: '#C8461F',
  /** Deep brand navy (accent 900) — active nav glyph body. */
  navy: accent[900],
  /** Deep end of a gradient that sits behind white text (avatar). Light = accent 900 like `navy`. */
  fillEnd: accent[900],
  /** Route line light end (accent 200) — the start of a drawn route. */
  routeLight: accent[200],
  /** "Vex" half of the wordmark (brand blue in Light; a readable lift in Graphite). */
  logoVex: accent[700],
};

/** Status */
export const status = {
  success: green[700],
  // Green as an ICON / data fill (dashboard cards): green-600. `success` (green-700) is the readable TEXT green.
  successIcon: green[600],
  /** Solid green FILL behind white text (the sign-in success beat). Same as successIcon in Light. */
  successFill: green[700], // colour system 2026-10-30: white label 5.5:1 (green-600 was 3.8)
  /** A finished SAVE beat's solid fill behind a white label (Light = `success`, green-700). */
  finishSave: green[700],
  /** A finished non-save beat's (delete / deactivate / security) solid fill behind a white label (Light = slate-600). */
  finishNeutral: slate[600],
  successSoft: green[100],
  successBorder: '#a7f3d0',
  // Route stop card (KAN-814): faint green wash + hairline for the visit box, ring around an opened done card.
  successFaint: green[50],
  successFaintBorder: green[100],
  successRing: green[200],
  warning: amber[600],
  /** SOLID amber FILL behind a white label (count badge, armed Stop). Light = `warning`. */
  warningFill: amber[700], // colour system 2026-10-30: white label 5.0:1 (amber-600 was 3.2)
  warningSoft: amber[100],
  warningBorder: '#fcd34d',
  // Amber action button (season "Iniciar cierre"): faint wash + 1.5px edge.
  warningFaint: amber[50],
  warningFaintBorder: amber[200],
  // Readable warning TEXT on warningSoft (≈7:1). `warning` (amber-600) is
  // ~2.9:1 there — fine for icons/bars, too faint for body copy.
  warningStrong: amber[800],
  // Amber as TEXT on white / warningSoft (KAN-793): amber-700 = 5.02:1 on #fff, 4.71:1 on #fff7e6.
  // `warning` (amber-600) stays for icons, bars and fills.
  warningText: amber[700],
  danger: red[600],
  dangerSoft: red[100],
  // Readable danger TEXT on dangerSoft. `danger` (red-600) is fine for icons/bars;
  // too faint for 12px pill/body copy on the soft wash (same job as warningStrong).
  dangerStrong: red[700],
  /** SOLID danger fill (button / pill) that carries white text; `danger` flips light in Graphite for text. */
  dangerFill: red[600],
  // Faintest red wash for a surface that must stay readable as paper (error toast, KAN-635).
  dangerFaint: red[50],
  dangerBorder: '#fecaca',
  // Red OUTLINE button edge (route stop "Sin servicio").
  dangerOutline: red[200],
  info: blue[700],
  /** SOLID blue-info FILL behind a white label (nav count, active chip). Light = `info`. */
  infoFill: blue[700],
  infoSoft: blue[100],
  infoBorder: '#bfdbfe',
  violet: violet[700],
  violetSoft: violet[100],
  violetBorder: '#ddd6fe',
  // Operations role — teal/cyan; distinct from admin-blue + worker-green
  operations: teal[700],
  operationsSoft: teal[100],
  operationsBorder: teal[200],
  // Row announced by a manual refresh (useRefreshDiffHighlights): green = new, amber = changed.
  successWash: '#f0faf5',
  successWashRing: 'rgba(5, 150, 105, 0.18)',
  warningWash: '#fdf6e7',
  warningWashRing: 'rgba(180, 83, 9, 0.14)',
};

/**
 * Role identity (RD-UI-028) — text/icon hues only. Deliberately disjoint from
 * the status colors (success/warning/danger/info): a role must never read as a status.
 */
export const role = {
  owner: slate[900],
  admin: indigo[700],
  supervisor: violet[700],
  operations: teal[700],
  billing: pink[700],
  worker: sky[700],
  platformRole: platformNavy[700],
};
/** Soft tint + border of each role hue (welcome-card icon tile). */
export const roleSoft = {
  owner: slate[100],
  admin: indigo[50],
  supervisor: violet[50],
  operations: teal[50],
  billing: pink[50],
  worker: sky[50],
  platformRole: platformNavy[50],
};
export const roleBorder = {
  owner: slate[200],
  admin: indigo[200],
  supervisor: '#ddd6fe',
  operations: teal[200],
  billing: pink[200],
  worker: sky[200],
  platformRole: platformNavy[200],
};

/** Elevation — subtle only, no blur */
export const elevation = {
  none: 'none',
  xs: '0 1px 2px rgba(15, 23, 42, 0.06)',
  sm: '0 1px 3px rgba(15, 23, 42, 0.07)',
  md: '0 4px 12px rgba(15, 23, 42, 0.09)',
  /** Dashboard widget rest/hover — soft two-layer with a faint brand-tinted ambient. */
  widget: '0 1px 2px rgba(15, 23, 42, 0.04), 0 10px 28px rgba(28, 90, 165, 0.05)',
  widgetHover: '0 2px 4px rgba(15, 23, 42, 0.05), 0 14px 36px rgba(28, 90, 165, 0.10)',
  elevated: '0 8px 24px rgba(15, 23, 42, 0.10)',
  floating: '0 16px 48px rgba(15, 23, 42, 0.14)',
  focusRing: `0 0 0 2px ${bg.surface}, 0 0 0 4px ${accent[200]}`,
  /** Canon text-field focus halo (KAN-856). Mirrored in src/index.css — keep in sync. */
  fieldFocusRing: `0 0 0 2px ${accent[100]}80`,
  fieldFocusRingDanger: `0 0 0 2px ${red[100]}80`,
  /** Toast card: ambient lift (dark ink in Light; black in Graphite — never a white glow). */
  toast: '0 18px 40px -8px rgba(15,23,42,0.32), 0 4px 12px rgba(15,23,42,0.12)',
  /** The 4px halo ring around a (non-error) toast. */
  toastHalo: '0 0 0 4px rgba(15,23,42,0.04)',
  /** Compact select: faint inset + a white 1px lip under it (Graphite: inset only). */
  fieldInset: 'inset 0 1px 2px rgba(15, 23, 42, 0.03), 0 1px 0 rgba(255, 255, 255, 0.9)',
};

/** Phone/tablet nav pill, desktop rail selected card and the header hairline. */
export const nav = {
  bar: 'rgba(241, 245, 249, 0.74)',
  barEdge: 'rgba(255, 255, 255, 0.55)',
  /** Inset top highlight of the glass bar. */
  barHighlight: 'rgba(255, 255, 255, 0.8)',
  barShadow: '0 0 0 0.5px rgba(15, 23, 42, 0.07), 0 2px 6px rgba(15, 23, 42, 0.06), 0 12px 28px -8px rgba(15, 23, 42, 0.22)',
  /** The raised selected card (every nav surface) — fill + shadow stack. */
  selectedCard: '#ffffff',
  selectedHighlight: 'rgba(255, 255, 255, 0.9)',
  selectedShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.9), 0 0 0 0.5px rgba(15, 23, 42, 0.10), 0 1px 2px rgba(15, 23, 42, 0.06), 0 4px 10px -4px rgba(15, 23, 42, 0.10)',
  /** Sub-tab switcher's gliding card (RD-UI-192): same as the nav card in light; one step lighter + hairline in dark. */
  segSelectedCard: '#ffffff',
  segSelectedShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.9), 0 0 0 0.5px rgba(15, 23, 42, 0.10), 0 1px 2px rgba(15, 23, 42, 0.06), 0 4px 10px -4px rgba(15, 23, 42, 0.10)',
  icon: slate[500],
  iconSelected: accent[900],
  track: slate[100],
  /** Hairline under the shell header (fades in on scroll). */
  headerEdge: 'rgba(15, 23, 42, 0.12)',
};

/** The ONE brand data ramp (charts, bars, donuts, capsules) — RD-DASH-011. */
export const dataRamp = {
  900: '#1c5aa5',
  600: '#4f84c9',
  400: '#93b4e0',
  200: '#cddcf0',
  track: '#eef2f6',
};

/** Dashboard card edge, row hairline, secondary-button outline — RD-DASH-011. */
export const homeEdge = {
  card: '#e6ebf1',
  hairline: '#eef2f6',
  button: '#dfe5ec',
};

/**
 * The legacy `uiStyles.ui.colors` palette (navy-neutral SaaS, pre-semantic). Light = its exact literals;
 * Graphite maps each to the matching dark role. New code uses the semantic groups, not this.
 */
export const legacy = {
  bg: '#f4f7fb',
  surface: '#ffffff',
  surfaceAlt: '#f8fafc',
  surfaceMuted: '#eef2f7',
  primary: '#1c5aa5',
  primaryDark: '#174d8e',
  primaryLight: '#b0ccf0',
  primarySoft: '#edf3fc',
  secondary: '#64748b',
  secondaryLight: '#cbd5e1',
  secondarySoft: '#f1f5f9',
  accent: '#1c5aa5',
  accentSoft: '#edf3fc',
  border: '#dbe4ef',
  borderLight: '#e8eef5',
  text: '#1e293b',
  textMuted: '#64748b',
  textLight: '#94a3b8',
  success: '#0f8f5f',
  successLight: '#9ad6bd',
  successSoft: '#ebfaf3',
  warning: '#f59e0b',
  warningText: '#b45309',
  warningLight: '#f5d28f',
  warningSoft: '#fff7e6',
  danger: '#c63f3f',
  dangerLight: '#efb2b2',
  dangerSoft: '#fdf1f1',
  purple: '#64748b',
  purpleSoft: '#f1f5f9',
  /** Menu avatar / icon-badge tint. */
  menuTint: '#eaf1f8',
};

/** Grouped lists (month / customer sections) — tints, rails and dividers. */
export const groupedList = {
  tint: '#f4f8fd',
  tintFaint: '#fafcff',
  ink: '#6b85ad',
  monthGlass: 'rgba(248, 250, 252, 0.94)',
  divider: '#edf1f7',
  rowHover: '#fafbfd',
};

/**
 * Shared transition strings.
 * IMPORTANT: avoid `all` — on iOS WebKit it triggers layout reflow on focus,
 * causing the virtual keyboard to dismiss immediately after opening.
 */
export const transition = {
  fast: 'border-color 150ms cubic-bezier(0.16,1,0.3,1), box-shadow 150ms cubic-bezier(0.16,1,0.3,1), background-color 150ms cubic-bezier(0.16,1,0.3,1), opacity 150ms cubic-bezier(0.16,1,0.3,1)',
  normal: 'border-color 200ms cubic-bezier(0.16,1,0.3,1), box-shadow 200ms cubic-bezier(0.16,1,0.3,1), background-color 200ms cubic-bezier(0.16,1,0.3,1), opacity 200ms cubic-bezier(0.16,1,0.3,1)',
  slow: 'border-color 300ms cubic-bezier(0.16,1,0.3,1), box-shadow 300ms cubic-bezier(0.16,1,0.3,1), background-color 300ms cubic-bezier(0.16,1,0.3,1), opacity 300ms cubic-bezier(0.16,1,0.3,1)',
};

export const semanticLiteral = {
  fontFamily,
  bg,
  fg,
  border,
  brand,
  status,
  role,
  roleSoft,
  roleBorder,
  elevation,
  nav,
  dataRamp,
  homeEdge,
  groupedList,
  legacy,
  ...extended,
  transition,
};
